import { useState } from 'react'
import { useCriarUsuario } from '../../../api/queries/usuarios'
import { Botao } from '../../../components/Botao'
import { Dialogo } from '../../../components/Dialogo'
import { AvisoDeErro } from '../../../components/Estados'
import { CampoDeSelecao, CampoDeTexto } from '../../../components/campos'
import { TEXTOS } from '../../../lib/glossario'
import type { Papel } from '../../../types/usuario'
import { SelecaoDePapeis } from './SelecaoDePapeis'

/**
 * O cadastro de uma conta.
 *
 * Os papéis vêm com a explicação por extenso ao lado de cada caixa, e não só com o nome: quem
 * concede precisa saber que "executor" é o papel de quem aplica em produção e "solicitante" o de
 * quem pede — no sistema anterior era tudo um perfil só, e quem executava homologava a si mesmo.
 *
 * A senha é digitada por quem cadastra e transmitida por fora do sistema, então nasce provisória:
 * a pessoa é obrigada a trocá-la no primeiro acesso.
 *
 * @param props o que fazer ao fechar
 * @returns o diálogo
 */
export function DialogoDeNovaConta({ aoFechar }: { aoFechar: () => void }) {
  const criar = useCriarUsuario()
  const [email, setEmail] = useState('')
  const [nome, setNome] = useState('')
  const [organizacao, setOrganizacao] = useState('SSTD')
  const [senha, setSenha] = useState('')
  const [papeis, setPapeis] = useState<Papel[]>(['LEITOR'])

  async function salvar() {
    try {
      await criar.mutateAsync({ email, nome, organizacaoSigla: organizacao, papeis, senhaProvisoria: senha })
      aoFechar()
    } catch {
      // O AvisoDeErro abaixo mostra o que o servidor respondeu e por quê.
    }
  }

  return (
    <Dialogo titulo={TEXTOS.usuarios.novaConta.titulo} aoFechar={aoFechar}>
      <div className="space-y-4">
        <p className="max-w-prose text-sm text-slate-600">{TEXTOS.usuarios.novaConta.ajuda}</p>

        <CampoDeTexto
          rotulo="Nome"
          ajuda="É o que aparece ao lado de cada ato que a pessoa registrar."
          value={nome}
          onChange={(e) => setNome(e.target.value)}
        />
        <CampoDeTexto
          rotulo="E-mail"
          ajuda="É por ele que a pessoa entra no sistema."
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <CampoDeSelecao
          rotulo="Organização"
          ajuda={TEXTOS.usuarios.organizacao}
          value={organizacao}
          onChange={(e) => {
            setOrganizacao(e.target.value)
            // Trocar para a contratada retira ADMIN do que já estava marcado: sem isto a caixa
            // sumiria da tela com o papel ainda no conjunto que vai para o servidor — que o
            // recusaria, mas depois de a pessoa achar que tinha cadastrado.
            if (e.target.value === 'DBSELLER') {
              setPapeis((atuais) => atuais.filter((papel) => papel !== 'ADMIN'))
            }
          }}
        >
          <option value="SSTD">Prefeitura de Niterói (SSTD)</option>
          <option value="DBSELLER">DBSeller (contratada)</option>
        </CampoDeSelecao>
        <CampoDeTexto
          rotulo="Senha provisória"
          ajuda={TEXTOS.usuarios.senhaProvisoria}
          type="text"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
        />

        <SelecaoDePapeis
          papeis={papeis}
          aoAlterar={setPapeis}
          daContratada={organizacao === 'DBSELLER'}
          desabilitado={criar.isPending}
        />

        {criar.isError && <AvisoDeErro erro={criar.error} titulo={TEXTOS.usuarios.naoCriou} />}

        <div className="flex flex-wrap gap-2">
          <Botao aparencia="primario" onClick={salvar} disabled={criar.isPending}>
            {criar.isPending ? 'Cadastrando…' : 'Cadastrar'}
          </Botao>
          <Botao onClick={aoFechar} disabled={criar.isPending}>
            Cancelar
          </Botao>
        </div>
      </div>
    </Dialogo>
  )
}
